/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {AfterViewInit, Component, EventEmitter, Input, OnInit, Output, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {CodeFilterModel} from './code-filter.model';
import {AbstractFilterComponent} from '../abstract-filter.component';
import {PrettyPrintReferencePipe} from '../../pipes/pretty-print-reference/pretty-print-reference.pipe';
import {InputTextComponent} from '../../input/input-text/input-text.component';
import {CodeFilterStateModel} from './code-filter-state.model';
import {ReplaySubject} from 'rxjs';
import {first} from 'rxjs/operators';
import {CodeFilterUtil} from './code-filter.util';
import {SpinnerService} from '../../spinner/spinner.service';
import {SpinnerContextTypeEnum} from '../../spinner/spinner-context-type.enum';

@Component({
    selector: 'app-code-filter',
    templateUrl: './code-filter.component.html',
    styleUrls: ['../filter.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CodeFilterComponent extends AbstractFilterComponent<CodeFilterStateModel> implements OnInit, AfterViewInit {

    @Input() public placeHolder: string;
    @Input() public codes: CodeFilterModel[] = null;
    @Input() public disableInputFilter: boolean;
    @Input() public hasEmptyOption = false;
    @Input() dataSubject: ReplaySubject<CodeFilterModel[]>;
    @Output() onOpen = new EventEmitter<void>();
    @ViewChild('searchInput') searchInput: InputTextComponent;

    public spinnerObservable = this.spinnerService.onLocalChange(this.onOpen);

    // TODO: This should be the root of all codes, including empty, because the state of a code always affects it as well.
    //  allCode
    //  | emptyCode
    //  + codes
    //    | child1
    //    + child2
    //      | grandchild1
    //      | grandchild2
    public allFilter = {
        code: 'ALL',
        name: 'All',
        checked: true,
        indeterminate: false
    } as CodeFilterModel;

    public emptyFilter = {
        code: 'EMPTY',
        name: 'Empty',
        checked: true,
        indeterminate: false
    } as CodeFilterModel;

    public showingEmptyOption: boolean;

    private initialSearchInputValue = new ReplaySubject<string>(1);

    constructor(private prettyPrintReferencePipe: PrettyPrintReferencePipe,
                public spinnerService: SpinnerService) {
        super();
    }

    ngOnInit(): void {
        this.showingEmptyOption = this.hasEmptyOption;

        this.dataSubject?.subscribe((result) => {
            this.spinnerService.stop(SpinnerContextTypeEnum.LOCAL, this.onOpen);
            this.codes = result;
            this.storeState({
                filteredCodes: result,
                all: true
            });
        });
    }

    ngAfterViewInit(): void {
        this.initialSearchInputValue.pipe(first()).subscribe(value => {
            if (this.searchInput != null) {
                this.searchInput.setValue(value);
            }
        });
    }

    onValueChanges(searchText: string): void {
        let filteredCodes: CodeFilterModel[];

        CodeFilterUtil.setCodeChecked(this.allFilter);

        if (searchText == null || searchText.trim().length === 0) {
            filteredCodes = this.codes;
            this.resetFilterCodesToSpecificCheckedState(true);

            CodeFilterUtil.setCodeChecked(this.emptyFilter);
            this.showingEmptyOption = true;
        } else {
            filteredCodes = this.filterCodes(searchText);

            this.showingEmptyOption = filteredCodes.some(code => code === this.emptyFilter);
            if (this.showingEmptyOption) {
                filteredCodes = filteredCodes.filter(code => code !== this.emptyFilter);
            }
        }

        this.storeState({
            searchText,
            filteredCodes,
            empty: this.showingEmptyOption ? this.emptyFilter.checked : null,
            all: true
        });
    }

    filterCodes = (query: string): CodeFilterModel[] => {
        const cleanQuery = query.trim();

        const toCompare: CodeFilterModel[] = (this.hasEmptyOption ? [...this.codes, this.emptyFilter] : this.codes) ?? [];
        const matchCodes: CodeFilterModel[] = [];
        for (const code of toCompare) {
            if (code.children) {
                for (const childCode of code.children) {
                    this.pushCodeIfMatches(cleanQuery, childCode, matchCodes);
                }
            } else {
                this.pushCodeIfMatches(cleanQuery, code, matchCodes);
            }
        }
        return matchCodes;
    };

    pushCodeIfMatches(codeQuery: string, toMatch: CodeFilterModel, toPush: CodeFilterModel[]): void {
        if ((toMatch.code + ' - ' + toMatch.name).toLowerCase().indexOf(codeQuery.toLowerCase()) > -1) {
            toMatch.checked = true;
            toPush.push(toMatch);
        }
    }

    getTooltipText(code: CodeFilterModel): string {
        return code.tooltipText != null ? code.tooltipText : this.prettyPrintReferencePipe.transform(code, this.formatWithoutCode);
    }

    getTooltipIsDynamic(code: CodeFilterModel): boolean {
        return code.tooltipText == null;
    }

    resetFilter(): void {
        super.resetFilter();
        this.storeState({
            filteredCodes: this.codes
        });

        if (!this.disableInputFilter) {
            this.searchInput.deleteInputValue();
        }
        this.resetFilterCodesToSpecificCheckedState(true);
        this.updateAllFilterCheckBoxStatusAfterToggle();
    }

    toggleAllCode(event: Event): void {
        const isChecked = (event.target as HTMLInputElement).checked;

        if (isChecked) {
            CodeFilterUtil.setCodeChecked(this.allFilter);
        } else {
            CodeFilterUtil.setCodeUnchecked(this.allFilter);
        }

        this.resetFilterCodesToSpecificCheckedState(isChecked);
        this.updateAllFilterCheckBoxStatusAfterToggle();
    }

    toggleEmptyCode(event: Event): void {
        const isChecked = (event.target as HTMLInputElement).checked;

        if (isChecked) {
            CodeFilterUtil.setCodeChecked(this.emptyFilter);
        } else {
            CodeFilterUtil.setCodeUnchecked(this.emptyFilter);
        }

        this.updateAllFilterCheckBoxStatusAfterToggle();

        this.storeState({
            empty: isChecked
        });
    }

    // This function is mutating state which may be difficult to work with state management frameworks in the future.
    toggleCode(code: CodeFilterModel, event: Event): void {
        code.checked = (event.target as HTMLInputElement).checked;
        if (code.children) {
            code.indeterminate = false;
            code.children.forEach(child => child.checked = code.checked);

        } else if (code.father) {
            let allChecked = true;
            let indeterminate = false;

            code.father.children.forEach(child => {
                allChecked = allChecked && child.checked;
                indeterminate = indeterminate || child.checked;
            });

            code.father.checked = allChecked;
            if (allChecked) {
                code.father.indeterminate = false;
            } else {
                code.father.indeterminate = indeterminate;
            }
        }

        this.updateAllFilterCheckBoxStatusAfterToggle();
    }

    resetFilterCodesToSpecificCheckedState(checked: boolean): void {
        if (this.hasEmptyOption) {
            this.emptyFilter.checked = checked;
        }

        this.codes?.forEach(code => {
            code.isOpen = false;
            code.checked = checked;
            code.indeterminate = false;
            if (code.children) {
                code.children.forEach(child => {
                    child.isOpen = false;
                    child.checked = checked;
                    child.indeterminate = false;
                });
            }
        });

        this.storeState({
            empty: this.emptyFilter.checked,
            filteredCodes: this.codes
        });
    }

    updateAllFilterCheckBoxStatusAfterToggle(): void {
        const codes = this.hasEmptyOption ? [...this.state.filteredCodes ?? [], this.emptyFilter] : this.state.filteredCodes;

        if (codes?.find(c => c.indeterminate === true)) {
            CodeFilterUtil.setCodeIndeterminate(this.allFilter);
        } else if (!codes?.find(c => c.checked === false)) {
            CodeFilterUtil.setCodeChecked(this.allFilter);
        } else if (!codes?.find(c => c.checked === true)) {
            CodeFilterUtil.setCodeUnchecked(this.allFilter);
        } else {
            CodeFilterUtil.setCodeIndeterminate(this.allFilter);
        }

        this.storeState({
            all: this.allFilter.indeterminate !== true ? this.allFilter.checked : null
        });
    }

    changeCodeFilterAccordionStatus(code: CodeFilterModel): void {
        code.isOpen = !code.isOpen;
    }

    emitSearch(): void {
        if (this.state.all === false) {
            // "as unknown as Event" instead of "as Event" prevents TS2352
            this.toggleAllCode({target: {checked: true} as HTMLInputElement} as unknown as Event);
        }

        this.filterEvent.emit(this.state);
    }

    startFilter(): void {
        if (this.state.filteredCodes == null) {
            if (this.dataSubject) {
                // Asynchronous data load
                this.spinnerService.start(SpinnerContextTypeEnum.LOCAL, this.onOpen);
                this.onOpen.emit();
            } else {
                // Synchronous data load (already passed from parent component)
                this.storeState({
                    filteredCodes: this.codes,
                    all: true
                });
            }
        }
        this.emit(this.dropdown.isOpen());
    }

    initFilter(state: CodeFilterStateModel): void {
        if (state) {
            this.initialSearchInputValue.next(state.searchText);

            if (state.empty) {
                CodeFilterUtil.setCodeChecked(this.emptyFilter);
            } else {
                CodeFilterUtil.setCodeUnchecked(this.emptyFilter);
            }

            if (state.all == null) {
                CodeFilterUtil.setCodeIndeterminate(this.allFilter);
            } else if (state.all === true) {
                CodeFilterUtil.setCodeChecked(this.allFilter);
            } else {
                CodeFilterUtil.setCodeUnchecked(this.allFilter);
            }

            this.storeState(state);
        }
    }

    storeState(state?: Partial<CodeFilterStateModel>): void {
        super.storeState(state);
        super.storeState({
            isActive: this.allFilter.indeterminate || this.allFilter.checked
                    && ((this.state.filteredCodes?.length ?? 0) !== (this.codes?.length ?? 0))
        });
    }
}
